import React, { useEffect, useState, useCallback, useMemo } from "react";
import "./styles.css";
import RecipeCard from "./components/RecipeCard";
import MarketCard from "./components/MarketCard";
import FloatingNotification from "./components/FloatingNotification";

interface Upgrade {
  name: string;
  cost: { [key: string]: number };
  description: string;
  purchased: boolean;
  maxLevel?: number;
  level?: number;
  tier: number;
  requires?: string[];
}

interface Achievement {
  id: string;
  name: string;
  description: string;
  condition: (stats: GameStats) => boolean;
  unlocked: boolean;
  reward: { type: string; amount: number };
}

interface GameStats {
  clicks: number;
  totalHarvested: number;
  totalWheatGrown: number;
  totalCoinsEarned: number;
  totalUpgradesPurchased: number;
  prestigeCount: number;
}

interface Weather {
  type: 'sunny' | 'rainy' | 'stormy' | 'drought';
  modifier: number;
  duration: number;
}

interface Recipe {
  name: string;
  ingredients: { [key: string]: number };
  output: { [key: string]: number };
  unlocked: boolean;
  type: string;
  requires?: string[];
}

interface Quest {
  id: string;
  name: string;
  description: string;
  requirements: { [key: string]: number };
  reward: { [key: string]: number };
  completed: boolean;
  tier: number;
  hidden?: boolean;
}

// FloatingText interface
interface FloatingText {
  id: string;
  text: string;
  x: number;
  y: number;
  color: string;
  timestamp: number;
}

// Update interfaces
interface MillPosition {
  row: number;
  col: number;
}

interface ScreenPosition {
  x: number;
  y: number;
}

interface MillData {
  id: string;
  active: boolean;
  progress: number;
  level: number;
  position: MillPosition;
}

interface SelectedMill {
  millId: string;
  screenPosition: ScreenPosition;
}


// Update MillMenu props interface
interface MillMenuProps {
  mill: MillData;
  screenPosition: ScreenPosition;
  onClose: () => void;
  onToggle: (id: string) => void;
  onUpgrade: (id: string) => void;
  inventory: { [key: string]: number };
}

// Update MillMenu component
const MillMenu: React.FC<MillMenuProps> = ({ 
  mill, 
  screenPosition, 
  onClose, 
  onToggle, 
  onUpgrade, 
  inventory 
}) => {
  const upgradeCost = {
    bricks: 3 + mill.level * 2,
    tools: 1 + Math.floor(mill.level / 2)
  };
  
  const canUpgrade = Object.entries(upgradeCost).every(
    ([resource, amount]) => inventory[resource] >= amount
  );

  const processTime = Math.max(50 - mill.level * 5, 10);
  
  return (
    <div 
      className="mill-dropdown"
      style={{ 
        top: `${screenPosition.y + 45}px`,
        left: `${screenPosition.x - 125}px`
      }}
      onClick={e => e.stopPropagation()}
    >
      <div className="mill-content">
        <div className="mill-header">
          <h4>Mill Level {mill.level}</h4>
          <button className="close-mill" onClick={onClose}>×</button>
        </div>

        <div className="mill-stats">
          <div>Speed: {processTime}s</div>
          <div>Output: {(1 + mill.level * 0.2).toFixed(1)}x</div>
        </div>

        <div className="mill-resources">
          <div>Wheat: {inventory.wheat}</div>
          <div>Flour: {inventory.flour}</div>
        </div>

        <div className="mill-progress-bar">
          <div 
            className="progress-fill"
            style={{ width: `${(mill.progress / processTime) * 100}%` }}
          />
        </div>

        <div className="mill-buttons">
          <button 
            className={`mill-toggle ${mill.active ? 'active' : ''}`}
            onClick={() => onToggle(mill.id)}
          >
            {mill.active ? 'Stop' : 'Start'} Mill
          </button>
          
          <button 
            className="mill-upgrade"
            onClick={() => onUpgrade(mill.id)}
            disabled={!canUpgrade}
          >
            Upgrade ({upgradeCost.bricks}🧱, {upgradeCost.tools}🔨)
          </button>
        </div>
      </div>
    </div>
  );
};

// Add to your interfaces section
interface OvenData {
  id: string;
  active: boolean;
  progress: number;
  level: number;
  position: MillPosition; // We can reuse MillPosition type
}

interface SelectedOven {
  ovenId: string;
  screenPosition: ScreenPosition;
}

interface OvenMenuProps {
  oven: OvenData;
  screenPosition: ScreenPosition;
  onClose: () => void;
  onToggle: (id: string) => void;
  onUpgrade: (id: string) => void;
  inventory: { [key: string]: number };
}

// Add new component for Oven Menu
const OvenMenu: React.FC<OvenMenuProps> = ({ 
  oven, 
  screenPosition, 
  onClose, 
  onToggle, 
  onUpgrade, 
  inventory 
}) => {
  const upgradeCost = {
    bricks: 3 + oven.level * 2,
    tools: 1 + Math.floor(oven.level / 2)
  };
  
  const canUpgrade = Object.entries(upgradeCost).every(
    ([resource, amount]) => inventory[resource] >= amount
  );

  const processTime = Math.max(40 - oven.level * 4, 8); // Slightly faster than mill
  
  return (
    <div 
      className="mill-dropdown" // We can reuse mill styles
      style={{ 
        top: `${screenPosition.y + 45}px`,
        left: `${screenPosition.x - 125}px`
      }}
      onClick={e => e.stopPropagation()}
    >
      <div className="mill-content">
        <div className="mill-header">
          <h4>Oven Level {oven.level}</h4>
          <button className="close-mill" onClick={onClose}>×</button>
        </div>

        <div className="mill-stats">
          <div>Speed: {processTime}s</div>
          <div>Output: {(1 + oven.level * 0.2).toFixed(1)}x</div>
        </div>

        <div className="mill-resources">
          <div>Flour: {inventory.flour}</div>
          <div>Bread: {inventory.bread}</div>
        </div>

        <div className="mill-progress-bar">
          <div 
            className="progress-fill"
            style={{ width: `${(oven.progress / processTime) * 100}%` }}
          />
        </div>

        <div className="mill-buttons">
          <button 
            className={`mill-toggle ${oven.active ? 'active' : ''}`}
            onClick={() => onToggle(oven.id)}
          >
            {oven.active ? 'Stop' : 'Start'} Oven
          </button>
          
          <button 
            className="mill-upgrade"
            onClick={() => onUpgrade(oven.id)}
            disabled={!canUpgrade}
          >
            Upgrade ({upgradeCost.bricks}🧱, {upgradeCost.tools}🔨)
          </button>
        </div>
      </div>
    </div>
  );
};

const App: React.FC = () => {
  // Modified initial grid generation
  const generateInitialGrid = () => {
    return Array.from({ length: 9 }, () => 
      Array.from({ length: 9 }, () => {
        const random = Math.random();
        if (random < 0.80) return "🌱";      // 60% chance for grass
        if (random < 0.95) return "🪨";      // 20% chance for rocks
        return Math.random() < 0.5 ? "🌲" : "🌳";  // 20% chance for trees (split between two types)
      })
    );
  };

  const initialGrid = generateInitialGrid();
  const [grid, setGrid] = useState<string[][]>(initialGrid);
  const [inventory, setInventory] = useState({
    fiber: 0,
    wheatSeeds: 0,
    clay: 0,
    rocks: 0,
    wheat: 0,
    coins: 0,
    gems: 0,
    wood: 0,
    bricks: 0,
    tools: 0,
    millStructure: 0,
    flour: 0,
    ovenStructure: 0,
    bread: 0,
    silo: 0,
    wheatReserve: 0,
    marketStall: 0
  });
  const [growthTimers, setGrowthTimers] = useState<{[key: string]: number}>({});
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const [upgrades, setUpgrades] = useState<Upgrade[]>([
    // Tier 1 - Basic Tools
    {
      name: "Stone Tools",
      cost: { rocks: 20 },
      description: "Reduces tilling cost by 3 fiber (Current: -0 fiber)",
      purchased: false,
      maxLevel: 3,
      level: 0,
      tier: 1
    },
    {
      name: "Quick Harvesting",
      cost: { fiber: 30 },
      description: "Double all harvest yields (wheat, seeds, fiber)",
      purchased: false,
      tier: 1
    },
    // Tier 2 - Basic Automation
    {
      name: "Auto Harvester",
      cost: { fiber: 100, clay: 15 },
      description: "Automatically harvests grown wheat",
      purchased: false,
      tier: 2,
      requires: ["Quick Harvesting"]
    },
    {
      name: "Auto Planter",
      cost: { wheat: 50, rocks: 30 },
      description: "Automatically plants seeds in tilled soil",
      purchased: false,
      tier: 2,
      requires: ["Quick Harvesting"]
    },
    {
      name: "Fast Planter",
      cost: { tools: 3, bricks: 5 },
      description: "Auto Planter works 2x faster",
      purchased: false,
      tier: 2,
      requires: ["Auto Planter"]
    },
    {
      name: "Smart Harvester",
      cost: { tools: 4, fiber: 200 },
      description: "Auto Harvester tills soil after harvesting",
      purchased: false,
      tier: 2,
      requires: ["Auto Harvester"]
    },
    // Tier 3 - Automation quests
    {
      name: "Weather Control",
      cost: { coins: 1000, rocks: 50 },
      description: "Influence weather patterns",
      purchased: false,
      tier: 3,
      requires: ["Auto Harvester"]
    },
    {
      name: "Field Expansion",
      cost: { coins: 500, clay: 100 },
      description: "Increase field size by 1",
      purchased: false,
      maxLevel: 3,
      level: 0,
      tier: 3,
      requires: ["Weather Control"]
    },
    {
      name: "Growth Accelerator",
      cost: { coins: 300, wheat: 100 },
      description: "Reduces growth time by 20%",
      purchased: false,
      maxLevel: 5,
      level: 0,
      tier: 3,
      requires: ["Weather Control"]
    },
    {
      name: "Clay Storage",
      cost: { clay: 50 },
      description: "Increases resource gains by 20%",
      purchased: false,
      maxLevel: 3,
      level: 0,
      tier: 3,
      requires: ["Weather Control"]
    },
    {
      name: "Rock Crusher",
      cost: { rocks: 30, clay: 20 },
      description: "Chance to get gems when harvesting rocks",
      purchased: false,
      tier: 3,
      requires: ["Weather Control"]
    },
    {
      name: "Multi-Harvest",
      cost: { fiber: 150, clay: 30 },
      description: "Harvest adjacent tiles automatically",
      purchased: false,
      tier: 3,
      requires: ["Weather Control"]
    },
    {
      name: "Advanced Irrigation",
      cost: { clay: 100, rocks: 50 },
      description: "Plants grow 50% faster in rainy weather",
      purchased: false,
      tier: 3,
      requires: ["Weather Control"]
    }
  ]);
  const [stats, setStats] = useState<GameStats>({
    clicks: 0,
    totalHarvested: 0,
    totalWheatGrown: 0,
    totalCoinsEarned: 0,
    totalUpgradesPurchased: 0,
    prestigeCount: 0
  });

  const [weather, setWeather] = useState<Weather>({ 
    type: 'sunny', 
    modifier: 1, 
    duration: 60 
  });
  
  const [achievements] = useState<Achievement[]>([
    {
      id: 'click-100',
      name: 'Busy Farmer',
      description: 'Click 100 times',
      condition: (stats) => stats.clicks >= 100,
      unlocked: false,
      reward: { type: 'coins', amount: 50 }
    },
    {
      id: 'wheat-1000',
      name: 'Wheat Master',
      description: 'Grow 1000 wheat',
      condition: (stats) => stats.totalWheatGrown >= 1000,
      unlocked: false,
      reward: { type: 'prestige', amount: 1 }
    },
    // Add more achievements...
  ]);

  const [recipes, setRecipes] = useState<Recipe[]>([
    // Basic Tools & Materials - Always visible
    {
      name: "Basic Tools",
      ingredients: { wood: 3, rocks: 2 },
      output: { tools: 1 },
      unlocked: true,
      type: "craft",
      requires: []
    },
    {
      name: "Bricks",
      ingredients: { clay: 3 },
      output: { bricks: 1 },
      unlocked: true,
      type: "craft",
      requires: []
    },
    // Progression Structures - Visible but locked initially
    {
      name: "Wheat Mill",
      ingredients: { bricks: 5, tools: 2 },
      output: { millStructure: 1 },
      unlocked: false,
      type: "craft",
      requires: ["Basic Tools"]
    },
    {
      name: "Baker's Oven",
      ingredients: { bricks: 8, tools: 3 },
      output: { ovenStructure: 1 },
      unlocked: false,
      type: "craft",
      requires: ["Wheat Mill"]
    },
    {
      name: "Storage Silo",
      ingredients: { bricks: 10, tools: 2 },
      output: { silo: 1 },
      unlocked: false,
      type: "craft",
      requires: ["Baker's Oven"]
    },
    {
      name: "Market Stall",
      ingredients: { wood: 10, tools: 2 },
      output: { marketStall: 1 },
      unlocked: false,
      type: "craft",
      requires: ["Storage Silo"]
    },
    // Market Trades - Unlocked when Market Stall is built
    {
      name: "Sell Wheat",
      ingredients: { wheat: 5 },
      output: { coins: 10 },
      unlocked: false,
      type: "market",
      requires: ["Market Stall"]
    },
    {
      name: "Sell Bread",
      ingredients: { bread: 1 },
      output: { coins: 25 },
      unlocked: false,
      type: "market",
      requires: ["Market Stall"]
    },
    {
      name: "Sell Flour",
      ingredients: { flour: 3 },
      output: { coins: 15 },
      unlocked: false,
      type: "market",
      requires: ["Market Stall"]
    }
  ]);

  const [completedUnclaimedQuests, setCompletedUnclaimedQuests] = useState<string[]>([]);
  const [claimingQuest, setClaimingQuest] = useState<string | null>(null);
  
  const [quests, setQuests] = useState<Quest[]>([
    // Tier 1 - Getting Started
    {
      id: "first-harvest",
      name: "First Harvest",
      description: "Harvest 5 fiber from grass to begin your journey",
      requirements: { fiber: 5 },
      reward: { wheatSeeds: 5 },
      completed: false,
      tier: 1,
      hidden: false
    },
    {
      id: "gather-basics",
      name: "Resource Gatherer",
      description: "Collect 10 wood and 10 rocks from the environment",
      requirements: { wood: 10, rocks: 10 },
      reward: { clay: 15 },
      completed: false,
      tier: 1,
      hidden: false
    },
    {
      id: "first-tools",
      name: "Tool Maker",
      description: "Craft your first set of Basic Tools",
      requirements: { tools: 1 },
      reward: { fiber: 30, rocks: 10 },
      completed: false,
      tier: 1,
      hidden: false
    },
    // Tier 2 - Farming & Building
    {
      id: "wheat-farmer",
      name: "Wheat Farmer",
      description: "Grow and harvest 20 wheat",
      requirements: { wheat: 20 },
      reward: { coins: 10, wheatSeeds: 10 },
      completed: false,
      tier: 2,
      hidden: false
    },
    {
      id: "brick-maker",
      name: "Brick by Brick",
      description: "Craft 15 bricks for construction",
      requirements: { bricks: 15 },
      reward: { tools: 2, wood: 10 },
      completed: false,
      tier: 2,
      hidden: false
    },
    {
      id: "build-mill",
      name: "Miller Time",
      description: "Build your first Wheat Mill",
      requirements: { millStructure: 1 },
      reward: { wheat: 30, coins: 20 },
      completed: false,
      tier: 2,
      hidden: false
    },
    // Tier 3 - Advanced Production
    {
      id: "flour-production",
      name: "Flour Power",
      description: "Produce 15 flour using your mill",
      requirements: { flour: 15 },
      reward: { bricks: 10, tools: 3 },
      completed: false,
      tier: 3,
      hidden: false
    },
    {
      id: "build-oven",
      name: "Master Baker",
      description: "Build the Baker's Oven",
      requirements: { ovenStructure: 1 },
      reward: { flour: 15, coins: 30 },
      completed: false,
      tier: 3,
      hidden: false
    },
    {
      id: "bread-production",
      name: "Bread Winner",
      description: "Bake 10 bread in your oven",
      requirements: { bread: 10 },
      reward: { coins: 50, gems: 1 },
      completed: false,
      tier: 3,
      hidden: false
    },
    // Tier 4 - Commerce & Expansion
    {
      id: "build-market",
      name: "Market Maker",
      description: "Build the Market Stall to start selling goods",
      requirements: { marketStall: 1 },
      reward: { coins: 75, bread: 5 },
      completed: false,
      tier: 4,
      hidden: false
    },
    {
      id: "first-sales",
      name: "First Profits",
      description: "Earn 200 coins from selling goods",
      requirements: { coins: 200 },
      reward: { gems: 2, tools: 5 },
      completed: false,
      tier: 4,
      hidden: false
    }
  ]);

  // Add quest tier tracking
  const [currentQuestTier, setCurrentQuestTier] = useState(1);

  // Modify quest checking to handle tiers
  useEffect(() => {
    const completedTierQuests = quests.filter(q => 
      q.tier === currentQuestTier && q.completed
    ).length;
    
    const totalTierQuests = quests.filter(q => 
      q.tier === currentQuestTier
    ).length;

    // If all quests in current tier are complete, unlock next tier
    if (completedTierQuests === totalTierQuests) {
      setCurrentQuestTier(prev => prev + 1);
      // Reveal next tier quests
      setQuests(prev => prev.map(quest => 
        quest.tier === currentQuestTier + 1 
          ? { ...quest, hidden: false }
          : quest
      ));
    }
  }, [quests, currentQuestTier]);

  // Add growth system
  useEffect(() => {
    const timer = setInterval(() => {
      setGrowthTimers(prev => {
        const newTimers = { ...prev };
        Object.keys(newTimers).forEach(key => {
          newTimers[key] = newTimers[key] + 1;
        });
        return newTimers;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Add auto-harvest system
  useEffect(() => {
    const hasAutoHarvester = upgrades.find(u => u.name === "Auto Harvester")?.purchased;
    if (!hasAutoHarvester) return;
    
    const hasSmartHarvester = upgrades.find(u => u.name === "Smart Harvester")?.purchased;
    const growthAccelerator = upgrades.find(u => u.name === "Growth Accelerator");
    const growthSpeed = growthAccelerator ? 1 - (0.2 * (growthAccelerator.level || 0)) : 1;

    const timer = setInterval(() => {
      setGrid(prevGrid => {
        let newGrid = prevGrid.map(row => [...row]);
        let harvested = false;
        
        for (let i = 0; i < newGrid.length; i++) {
          for (let j = 0; j < newGrid[i].length; j++) {
            if (newGrid[i][j] === "🌾") {
              const key = `${i}-${j}`;
              const adjustedGrowthTime = 5 * growthSpeed / (weather.modifier * prestigeMultiplier);
              
              // Only harvest if wheat is fully grown
              if (growthTimers[key] >= adjustedGrowthTime) {
                const quickHarvesting = upgrades.find(u => u.name === "Quick Harvesting")?.purchased;
                const resourceMultiplier = 1 + (upgrades.find(u => u.name === "Clay Storage")?.level || 0) * 0.2;
                const coinsEarned = 1;
                
                setInventory(prev => ({
                  ...prev,
                  wheat: prev.wheat + Math.ceil((Math.floor(Math.random() * 3) + 1) * resourceMultiplier) * (quickHarvesting ? 2 : 1),
                  wheatSeeds: prev.wheatSeeds + Math.ceil((Math.floor(Math.random() * 2)) * resourceMultiplier) * (quickHarvesting ? 2 : 1),
                  coins: prev.coins + coinsEarned
                }));
                setStats(prev => ({
                  ...prev,
                  totalCoinsEarned: prev.totalCoinsEarned + coinsEarned
                }));
                
                // Check if Smart Harvester should till after harvesting
                const stoneTools = upgrades.find(u => u.name === "Stone Tools");
                const tillingCost = Math.max(1, 10 - ((stoneTools?.level || 0) * 3));
                
                if (hasSmartHarvester && inventory.fiber >= tillingCost) {
                  // Auto-till after harvesting
                  setInventory(prev => ({
                    ...prev,
                    fiber: Math.max(0, prev.fiber - tillingCost)
                  }));
                  newGrid[i][j] = "🟤"; // Tilled soil
                } else {
                  // Just leave as dirt
                  newGrid[i][j] = "🟫";
                }
                
                // Clean up growth timer
                setGrowthTimers(prev => {
                  const newTimers = {...prev};
                  delete newTimers[`${i}-${j}`];
                  return newTimers;
                });
                
                harvested = true;
              }
            }
          }
        }
        
        if (harvested) {
          setStats(prev => ({
            ...prev,
            totalHarvested: prev.totalHarvested + 1
          }));
          return newGrid;
        }
        return prevGrid;
      });
    }, 5000); // Check every 5 seconds instead of 2

    return () => clearInterval(timer);
  }, [upgrades, inventory.fiber, growthTimers, weather]);

  // Add auto-plant system
  useEffect(() => {
    const hasAutoPlanter = upgrades.find(u => u.name === "Auto Planter")?.purchased;
    if (!hasAutoPlanter) return;
    
    const hasFastPlanter = upgrades.find(u => u.name === "Fast Planter")?.purchased;
    const plantSpeed = hasFastPlanter ? 1500 : 3000; // 2x faster with upgrade

    const timer = setInterval(() => {
      if (inventory.wheatSeeds <= 0) return;
      
      setGrid(prevGrid => {
        let newGrid = prevGrid.map(row => [...row]);
        let planted = false;

        for (let i = 0; i < newGrid.length; i++) {
          for (let j = 0; j < newGrid[i].length; j++) {
            // Check for tilled soil emoji 🟤
            if (newGrid[i][j] === "🟤") {
              setInventory(prev => {
                if (prev.wheatSeeds <= 0) return prev;
                return {...prev, wheatSeeds: prev.wheatSeeds - 1};
              });
              newGrid[i][j] = "🌾"; // Plant wheat
              setGrowthTimers(prev => ({...prev, [`${i}-${j}`]: 0}));
              planted = true;
              break;
            }
          }
          if (planted) break;
        }
        
        return planted ? newGrid : prevGrid;
      });
    }, plantSpeed);

    return () => clearInterval(timer);
  }, [upgrades, inventory.wheatSeeds]);

  // Add weather system
  useEffect(() => {
    const weatherTimer = setInterval(() => {
      const weatherTypes: Weather[] = [
        { type: 'sunny', modifier: 1, duration: 60 },
        { type: 'rainy', modifier: 1.5, duration: 30 },
        { type: 'stormy', modifier: 0.5, duration: 20 },
        { type: 'drought', modifier: 0.25, duration: 40 }
      ];
      
      const hasWeatherControl = upgrades.find(u => u.name === "Weather Control")?.purchased;
      if (hasWeatherControl) {
        // Better weather chances with upgrade
        setWeather(weatherTypes.filter(w => w.modifier >= 1)[
          Math.floor(Math.random() * 2)
        ]);
      } else {
        setWeather(weatherTypes[Math.floor(Math.random() * weatherTypes.length)]);
      }
    }, weather.duration * 1000);

    return () => clearInterval(weatherTimer);
  }, [weather.duration, upgrades]);

  // Add prestige system
  const [prestigePoints, setPrestigePoints] = useState(0);
  const [prestigeMultiplier, setPrestigeMultiplier] = useState(1);

  const prestige = () => {
    if (inventory.wheat >= 1000) {
      setPrestigePoints(prev => prev + Math.floor(inventory.wheat / 1000));
      setPrestigeMultiplier(prev => prev + 0.1);
      
      // Reset game but keep prestige-related stats
      setInventory({
        fiber: 0,
        wheatSeeds: 10,
        clay: 0,
        rocks: 0,
        wheat: 0,
        coins: Math.floor(inventory.coins * 0.1),
        gems: Math.floor(inventory.gems * 0.25),
        bricks: 0,
        tools: 0,
        millStructure: 0,
        flour: 0,
        ovenStructure: 0,
        bread: 0,
        silo: 0,
        wheatReserve: 0,
        marketStall: 0,
        wood: 0
      });
      
      setGrid(initialGrid);
      setUpgrades(prev => prev.map(u => ({...u, purchased: false, level: 0})));
      setStats(prev => ({
        ...prev,
        prestigeCount: prev.prestigeCount + 1
      }));
    }
  };

  // Modified growth system with weather and prestige effects
  useEffect(() => {
    const timer = setInterval(() => {
      setGrid((prevGrid) => {
        let newGrid = prevGrid.map((row) => [...row]);
        let updated = false;
        
        const growthAccelerator = upgrades.find(u => u.name === "Growth Accelerator");
        const growthSpeed = growthAccelerator ? 
          1 - (0.2 * (growthAccelerator.level || 0)) : 1;
        
        for (let i = 0; i < newGrid.length; i++) {
          for (let j = 0; j < newGrid[i].length; j++) {
            const key = `${i}-${j}`;
            const adjustedGrowthTime = 5 * growthSpeed / (weather.modifier * prestigeMultiplier);
            
            if (newGrid[i][j] === "\uD83C\uDF3E" && growthTimers[key] >= adjustedGrowthTime) {
              newGrid[i][j] = "🌾";
              updated = true;
              setStats(prev => ({
                ...prev,
                totalWheatGrown: prev.totalWheatGrown + 1
              }));
            }
          }
        }
        
        if (updated) return newGrid;
        return prevGrid;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [growthTimers, weather.modifier, prestigeMultiplier, upgrades]);

  // Add field expansion logic
  useEffect(() => {
    const expansion = upgrades.find(u => u.name === "Field Expansion");
    if (expansion?.level) {
      const newSize = 9 + expansion.level;
      setGrid(prev => {
        const newGrid = Array.from(
          { length: newSize }, 
          () => Array(newSize).fill("\uD83C\uDF31")
        );
        
        // Copy existing field
        prev.forEach((row, i) => {
          row.forEach((cell, j) => {
            newGrid[i][j] = cell;
          });
        });
        
        return newGrid;
      });
    }
  }, [upgrades]);

  // Add these near the top of the App component, after the state declarations
  const rockCrusher = upgrades.find(u => u.name === "Rock Crusher")?.purchased;
  const multiHarvest = upgrades.find(u => u.name === "Multi-Harvest")?.purchased;
  const stoneTools = upgrades.find(u => u.name === "Stone Tools");
  const tillingCost = 10 - (stoneTools?.level || 0) * 3;
  const resourceMultiplier = 1 + (upgrades.find(u => u.name === "Clay Storage")?.level || 0) * 0.2;

  // At the top with other state declarations
  const [placingStructure, setPlacingStructure] = useState<string | null>(null);
  const [hoveredCell, setHoveredCell] = useState<{row: number, col: number} | null>(null);

  // Memoized getEmoji function
  const getEmoji = useCallback((resource: string) => {
    const emojiMap: { [key: string]: string } = {
      wood: "🪵",
      rocks: "🪨",
      clay: "🟤",
      tools: "🔨",
      bricks: "🧱",
      flour: "❄️",
      millStructure: "⚙️",
      ovenStructure: "🏭",
      silo: "🏗️",
      marketStall: "🏪",
      wheat: "🌾",
      bread: "🍞",
      fiber: "🌿",
      wheatSeeds: "🌱"
    };
    return emojiMap[resource] || "";
  }, []);

  // Modify the original craftRecipe function instead of declaring a new one
  const craftRecipe = useCallback((recipe: Recipe) => {
    // Check if this is a structure recipe
    if (recipe.output.millStructure || recipe.output.ovenStructure || 
        recipe.output.silo || recipe.output.marketStall) {
      setSelectedAction(null); // Clear any other selected action
      setPlacingStructure(Object.keys(recipe.output)[0]);
      return;
    }

    const canCraft = Object.entries(recipe.ingredients).every(
      ([resource, amount]) => inventory[resource as keyof typeof inventory] >= amount
    );

    if (!canCraft) return;

    const marketBonus = upgrades.find(u => u.name === "Market Trading")?.purchased ? 1.5 : 1;

    setInventory(prev => {
      const newInventory = {...prev};
      // Deduct ingredients
      Object.entries(recipe.ingredients).forEach(([resource, amount]) => {
        const key = resource as keyof typeof inventory;
        newInventory[key] -= amount;
      });
      // Add outputs with market bonus
      Object.entries(recipe.output).forEach(([resource, amount]) => {
        const key = resource as keyof typeof inventory;
        newInventory[key] += Math.floor(amount * marketBonus);
      });
      return newInventory;
    });
  }, [inventory, upgrades, recipes]);

  // Update the handleTileClick function to include rock and tree harvesting
  const handleTileClick = (rowIndex: number, cellIndex: number, event: React.MouseEvent) => {
    const currentTile = grid[rowIndex][cellIndex];
    const newGrid = [...grid];

    // Check for oven interaction
    if (currentTile === "🏭") {
      const oven = ovens.find(o => 
        o.position.row === rowIndex && o.position.col === cellIndex
      );
      if (oven) {
        setSelectedOven({
          ovenId: oven.id,
          screenPosition: { x: event.clientX, y: event.clientY }
        });
      }
      return;
    }

    // Handle structure placement
    if (placingStructure) {
      if (currentTile === "🟫") {
        if (placingStructure === "millStructure") {
          const newMill: MillData = {
            id: `mill-${Date.now()}-${rowIndex}-${cellIndex}`,
            active: false,
            progress: 0,
            level: 1,
            position: { row: rowIndex, col: cellIndex }
          };
          setMills(prev => [...prev, newMill]);
          
          // Update the grid
          newGrid[rowIndex][cellIndex] = "⚙️";
          
          // Find and use the mill recipe to deduct resources
          const millRecipe = recipes.find(r => r.output.millStructure);
          if (millRecipe) {
            setInventory(prev => {
              const newInventory = {...prev};
              Object.entries(millRecipe.ingredients).forEach(([resource, amount]) => {
                const key = resource as keyof typeof inventory;
                newInventory[key] = Math.max(0, newInventory[key] - amount);
              });
              return newInventory;
            });
          }
          setPlacingStructure(null);
        } else if (placingStructure === "ovenStructure") {
          const newOven: OvenData = {
            id: `oven-${Date.now()}-${rowIndex}-${cellIndex}`,
            active: false,
            progress: 0,
            level: 1,
            position: { row: rowIndex, col: cellIndex }
          };
          setOvens(prev => [...prev, newOven]);
          newGrid[rowIndex][cellIndex] = "🏭";
          
          // Deduct resources for oven
          const ovenRecipe = recipes.find(r => r.output.ovenStructure);
          if (ovenRecipe) {
            setInventory(prev => {
              const newInventory = {...prev};
              Object.entries(ovenRecipe.ingredients).forEach(([resource, amount]) => {
                const key = resource as keyof typeof inventory;
                newInventory[key] = Math.max(0, newInventory[key] - amount);
              });
              return newInventory;
            });
          }
          setPlacingStructure(null);
        } else if (placingStructure === "silo") {
          newGrid[rowIndex][cellIndex] = "🏗️";
          
          // Deduct resources for silo
          const siloRecipe = recipes.find(r => r.output.silo);
          if (siloRecipe) {
            setInventory(prev => {
              const newInventory = {...prev};
              Object.entries(siloRecipe.ingredients).forEach(([resource, amount]) => {
                const key = resource as keyof typeof inventory;
                newInventory[key] = Math.max(0, newInventory[key] - amount);
              });
              newInventory.silo += 1;
              return newInventory;
            });
          }
          setPlacingStructure(null);
        } else if (placingStructure === "marketStall") {
          newGrid[rowIndex][cellIndex] = "🏪";
          
          // Deduct resources for market stall
          const marketRecipe = recipes.find(r => r.output.marketStall);
          if (marketRecipe) {
            setInventory(prev => {
              const newInventory = {...prev};
              Object.entries(marketRecipe.ingredients).forEach(([resource, amount]) => {
                const key = resource as keyof typeof inventory;
                newInventory[key] = Math.max(0, newInventory[key] - amount);
              });
              newInventory.marketStall += 1;
              return newInventory;
            });
          }
          setPlacingStructure(null);
        }
        setGrid(newGrid);
        setHoveredCell(null);
        
        // Add placement success feedback
        addFloatingText('✅ Placed!', event.clientX, event.clientY, '#4CAF50');
        return;
      }
      return;
    }

    // Check for mill interaction first, before any other actions
    if (grid[rowIndex][cellIndex] === "⚙️") {
      const mill = mills.find(m => 
        m.position.row === rowIndex && m.position.col === cellIndex
      );
      if (mill) {
        const rect = (event.target as HTMLElement).getBoundingClientRect();
        setSelectedMill({
          millId: mill.id,
          screenPosition: {
            x: rect.left,
            y: rect.top
          }
        });
        return;
      }
    }

    if (!selectedAction) return;

    setGrid(prevGrid => {
      const newGrid = prevGrid.map(row => [...row]);
      const currentTile = newGrid[rowIndex][cellIndex];
      
      if (selectedAction === "harvest") {
        if (currentTile === "🌿" || currentTile === "🌱") { // Tall grass
          const quickHarvesting = upgrades.find(u => u.name === "Quick Harvesting")?.purchased;
          const fiberGain = Math.random() > 0.4 ? Math.ceil(1 * resourceMultiplier) * (quickHarvesting ? 2 : 1) : 0;
          const seedGain = Math.random() > 0.5 ? Math.ceil(1 * resourceMultiplier) * (quickHarvesting ? 2 : 1) : 0;
          
          if (fiberGain) {
            addFloatingText(`+${fiberGain} 🌿`, event.clientX, event.clientY, '#4CAF50');
          }
          if (seedGain) {
            addFloatingText(`+${seedGain} 🌱`, event.clientX, event.clientY + 20, '#4CAF50');
          }
          
          setInventory(prev => ({
            ...prev,
            fiber: prev.fiber + fiberGain,
            wheatSeeds: prev.wheatSeeds + seedGain
          }));
          newGrid[rowIndex][cellIndex] = "🟫";
        }
        else if (currentTile === "🌾") { // Grown wheat
          const key = `${rowIndex}-${cellIndex}`;
          const growthAccelerator = upgrades.find(u => u.name === "Growth Accelerator");
          const growthSpeed = growthAccelerator ? 
            1 - (0.2 * (growthAccelerator.level || 0)) : 1;
          const adjustedGrowthTime = 5 * growthSpeed / (weather.modifier * prestigeMultiplier);
          
          // Add this check to ensure wheat is fully grown
          if (growthTimers[key] >= adjustedGrowthTime) {
            const quickHarvesting = upgrades.find(u => u.name === "Quick Harvesting")?.purchased;
            const wheatYield = Math.ceil((Math.floor(Math.random() * 3 + 1) * resourceMultiplier)) * (quickHarvesting ? 2 : 1);
            const seedYield = Math.ceil((Math.floor(Math.random() * 2) * resourceMultiplier)) * (quickHarvesting ? 2 : 1);

            addFloatingText(`+${wheatYield} 🌾`, event.clientX, event.clientY, '#FFD700');
            if (seedYield) {
              addFloatingText(`+${seedYield} 🌱`, event.clientX, event.clientY + 20, '#4CAF50');
            }

            setInventory(prev => ({
              ...prev,
              wheat: prev.wheat + wheatYield,
              wheatSeeds: prev.wheatSeeds + seedYield
            }));
            
            // Change to dirt (🟫) after harvesting
            newGrid[rowIndex][cellIndex] = "🟫";
            
            // Clean up the growth timer
            setGrowthTimers(prev => {
              const newTimers = {...prev};
              delete newTimers[key];
              return newTimers;
            });
          }
        }
        else if (currentTile === "🪨") { // Rocks
          const rockGain = Math.ceil(1 * resourceMultiplier);
          const clayChance = Math.random();
          const clayGain = clayChance < 0.4 ? Math.ceil(1 * resourceMultiplier) : 0; // 40% chance for clay
          
          addFloatingText(`+${rockGain} 🪨`, event.clientX, event.clientY, '#808080');
          if (clayGain) {
            addFloatingText(`+${clayGain} 🟤`, event.clientX, event.clientY + 20, '#4444FF');
          }
          
          setInventory(prev => ({
            ...prev,
            rocks: prev.rocks + rockGain,
            clay: prev.clay + clayGain
          }));
          newGrid[rowIndex][cellIndex] = "🟫";
        }
        else if (currentTile === "🌲" || currentTile === "🌳") { // Trees
          const woodGain = Math.ceil(1 * resourceMultiplier);
          addFloatingText(`+${woodGain} 🪵`, event.clientX, event.clientY, '#8B4513');
          
          setInventory(prev => ({
            ...prev,
            wood: prev.wood + woodGain
          }));
          newGrid[rowIndex][cellIndex] = "🟫";
        }
      }
      else if (selectedAction === "till" && (currentTile === "🟫" || currentTile === "🌱")) {
        const stoneTools = upgrades.find(u => u.name === "Stone Tools");
        const tillingCost = Math.max(1, 10 - ((stoneTools?.level || 0) * 3));

        if (inventory.fiber >= tillingCost) {
          addFloatingText(`-${tillingCost} 🌿`, event.clientX, event.clientY, '#FF4444');
          
          setInventory(prev => ({
            ...prev,
            fiber: Math.max(0, prev.fiber - tillingCost) // Prevent negative
          }));
          newGrid[rowIndex][cellIndex] = "🟤";
        }
      }
      else if (selectedAction === "plant" && currentTile === "🟤") {
        if (inventory.wheatSeeds > 0) {
          addFloatingText(`-1 🌱`, event.clientX, event.clientY, '#FF4444');
          
          setInventory(prev => ({
            ...prev,
            wheatSeeds: Math.max(0, prev.wheatSeeds - 1) // Prevent negative
          }));
          newGrid[rowIndex][cellIndex] = "🌾";
          setGrowthTimers(prev => ({...prev, [`${rowIndex}-${cellIndex}`]: 0}));
        }
      }

      return newGrid;
    });
  };

  // Update the purchase upgrade function
  const purchaseUpgrade = useCallback((upgrade: Upgrade) => {
    // Check if already purchased (for non-leveled upgrades)
    if (upgrade.purchased && !upgrade.maxLevel) {
      return;
    }
    
    // Check if at max level (for leveled upgrades)
    if (upgrade.maxLevel && (upgrade.level || 0) >= upgrade.maxLevel) {
      return;
    }
    
    const canAfford = Object.entries(upgrade.cost).every(
      ([resource, amount]) => inventory[resource as keyof typeof inventory] >= amount
    );

    if (!canAfford) return;

    setInventory(prev => {
      const newInventory = {...prev};  // This correctly spreads all properties including gems
      Object.entries(upgrade.cost).forEach(([resource, amount]) => {
        // Use type assertion to ensure TypeScript knows this is a valid key
        const key = resource as keyof typeof inventory;
        newInventory[key] = newInventory[key] - amount;
      });
      return newInventory;
    });

    setUpgrades(prev => 
      prev.map(u => {
        if (u.name === upgrade.name) {
          const newLevel = (u.level || 0) + 1;
          return {
            ...u, 
            purchased: true, 
            level: newLevel,
            description: u.name === "Stone Tools" 
              ? `Reduces tilling cost by 3 fiber (Current: -${newLevel * 3} fiber)`
              : u.description
          };
        }
        return u;
      })
    );

    setStats(prev => ({
      ...prev,
      totalUpgradesPurchased: prev.totalUpgradesPurchased + 1
    }));
  }, [inventory, upgrades, stats]);

  // Modify growth system to include Advanced Irrigation
  useEffect(() => {
    const timer = setInterval(() => {
      const advancedIrrigation = upgrades.find(u => u.name === "Advanced Irrigation")?.purchased;
      const weatherModifier = weather.type === 'rainy' && advancedIrrigation ? 
        weather.modifier * 1.5 : weather.modifier;

      setGrid((prevGrid) => {
        let newGrid = prevGrid.map((row) => [...row]);
        let updated = false;
        
        const growthAccelerator = upgrades.find(u => u.name === "Growth Accelerator");
        const growthSpeed = growthAccelerator ? 
          1 - (0.2 * (growthAccelerator.level || 0)) : 1;
        
        for (let i = 0; i < newGrid.length; i++) {
          for (let j = 0; j < newGrid[i].length; j++) {
            const key = `${i}-${j}`;
            const adjustedGrowthTime = 5 * growthSpeed / (weatherModifier * prestigeMultiplier);
            
            if (newGrid[i][j] === "\uD83C\uDF3E" && growthTimers[key] >= adjustedGrowthTime) {
              newGrid[i][j] = "🌾";
              updated = true;
              setStats(prev => ({
                ...prev,
                totalWheatGrown: prev.totalWheatGrown + 1
              }));
            }
          }
        }
        
        if (updated) return newGrid;
        return prevGrid;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [growthTimers, weather, upgrades]);

  // Add quest checking function - mark as ready to claim but don't auto-award
  useEffect(() => {
    quests.forEach(quest => {
      if (quest.completed) return;

      const requirementsMet = Object.entries(quest.requirements).every(
        ([resource, amount]) => {
          if (resource === 'farmingUpgrades') {
            const farmingUpgrades = ['Quick Harvesting', 'Auto Harvester'];
            return farmingUpgrades.filter(name => 
              upgrades.find(u => u.name === name)?.purchased
            ).length >= amount;
          }
          return inventory[resource as keyof typeof inventory] >= amount;
        }
      );

      if (requirementsMet && !completedUnclaimedQuests.includes(quest.id)) {
        // Mark as ready to claim
        setCompletedUnclaimedQuests(prev => [...prev, quest.id]);
      }
    });
  }, [inventory, upgrades, quests, completedUnclaimedQuests]);
  
  // Claim quest rewards
  const claimQuest = useCallback((questId: string) => {
    const quest = quests.find(q => q.id === questId);
    if (!quest) return;
    
    // Scroll to quest section
    const questSection = document.querySelector('.quest-section');
    if (questSection) {
      questSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    
    // Start claiming animation
    setClaimingQuest(questId);
    
    // Award rewards after animation starts
    setTimeout(() => {
      setInventory(prev => {
        const newInventory = {...prev};
        Object.entries(quest.reward).forEach(([resource, amount]) => {
          const key = resource as keyof typeof inventory;
          newInventory[key] += amount;
        });
        return newInventory;
      });
      
      // Mark quest as completed
      setQuests(prev => 
        prev.map(q => q.id === questId ? {...q, completed: true} : q)
      );
      
      // Remove from unclaimed
      setCompletedUnclaimedQuests(prev => prev.filter(id => id !== questId));
      
      // Clear claiming animation
      setTimeout(() => {
        setClaimingQuest(null);
      }, 1500);
    }, 300);
  }, [quests]);

  // Add regrowth system for grass
  useEffect(() => {
    const timer = setInterval(() => {
      setGrid(prevGrid => {
        let newGrid = prevGrid.map(row => [...row]);
        let updated = false;

        for (let i = 0; i < newGrid.length; i++) {
          for (let j = 0; j < newGrid[i].length; j++) {
            if (newGrid[i][j] === "🟫") { // Empty tile
              const random = Math.random();
              if (random < 0.05) { // 5% chance of regrowth
                // Determine what grows
                const growthRandom = Math.random();
                if (growthRandom < 0.80) { // 80% chance for grass
                  newGrid[i][j] = "🌱";
                  updated = true;
                } else if (growthRandom < 0.95) { // 15% chance for rocks
                  newGrid[i][j] = "🪨";
                  updated = true;
                } else { // 5% chance for trees
                  newGrid[i][j] = Math.random() < 0.5 ? "🌲" : "🌳";
                  updated = true;
                }
              }
            }
          }
        }

        return updated ? newGrid : prevGrid;
      });
    }, 10000); // Check every 10 seconds

    return () => clearInterval(timer);
  }, []);

  // Update the GrowthProgress component
  const GrowthProgress: React.FC<{row: number, col: number}> = ({row, col}) => {
    const key = `${row}-${col}`;
    const timer = growthTimers[key] || 0;
    const growthAccelerator = upgrades.find(u => u.name === "Growth Accelerator");
    const growthSpeed = growthAccelerator ? 
      1 - (0.2 * (growthAccelerator.level || 0)) : 1;
    const adjustedGrowthTime = 5 * growthSpeed / (weather.modifier * prestigeMultiplier);
    const progress = Math.min((timer / adjustedGrowthTime) * 100, 100);

    return (
      <div className="growth-progress">
        <div 
          className={`growth-progress-bar progress-${Math.round(progress / 10) * 10}`}
        />
      </div>
    );
  };

  // Update the recipe unlock conditions
  useEffect(() => {
    setRecipes(prev => prev.map(recipe => {
      // Check if all required structures/items are present
      const requirementsMet = !recipe.requires || recipe.requires.every(req => {
        switch (req) {
          case "Wheat Mill": return inventory.millStructure > 0;
          case "Baker's Oven": return inventory.ovenStructure > 0;
          case "Storage Silo": return inventory.silo > 0;
          case "Market Stall": return inventory.marketStall > 0;
          case "Basic Tools": return inventory.tools > 0;
          default: return true;
        }
      });

      return { ...recipe, unlocked: requirementsMet };
    }));
  }, [inventory]);

  // Add hover effect for structure placement
  const getCellStyle = (cell: string, rowIndex: number, cellIndex: number) => {
    if (placingStructure && cell === "🟫") {
      return {
        backgroundColor: '#90EE90',
        cursor: 'pointer'
      };
    }
    return {};
  };

  const sellItem = useCallback((recipe: Recipe) => {
    const canSell = Object.entries(recipe.ingredients).every(
      ([resource, amount]) => inventory[resource as keyof typeof inventory] >= amount
    );

    if (!canSell) return;

    // Apply market trading bonus if upgrade is purchased
    const marketBonus = upgrades.find(u => u.name === "Market Trading")?.purchased ? 1.5 : 1;

    setInventory(prev => {
      const newInventory = {...prev};
      
      // Remove sold items
      Object.entries(recipe.ingredients).forEach(([resource, amount]) => {
        const key = resource as keyof typeof inventory;
        newInventory[key] -= amount;
      });
      
      // Add received items with market bonus
      Object.entries(recipe.output).forEach(([resource, amount]) => {
        const key = resource as keyof typeof inventory;
        newInventory[key] += Math.floor(amount * marketBonus);
      });

      return newInventory;
    });

    // Update stats
    setStats(prev => ({
      ...prev,
      totalCoinsEarned: prev.totalCoinsEarned + 
        (recipe.output.coins ? Math.floor(recipe.output.coins * (upgrades.find(u => u.name === "Market Trading")?.purchased ? 1.5 : 1)) : 0)
    }));
  }, [inventory, upgrades, stats]);

  // Add to App component state
  const [floatingTexts, setFloatingTexts] = useState<FloatingText[]>([]);
 
  // Update the addFloatingText function with a limit
  const addFloatingText = useCallback((text: string, x: number, y: number, color: string = '#4CAF50') => {
    const id = Date.now().toString() + Math.random();
    setFloatingTexts(prev => {
      // Keep only the most recent 20 notifications
      const newTexts = [...prev, { id, text, x, y, color, timestamp: Date.now() }];
      return newTexts.slice(-20);
    });
  }, []);

  // Update the cleanup effect
  useEffect(() => {
    const cleanup = setInterval(() => {
      const now = Date.now();
      setFloatingTexts(prev => {
        const filtered = prev.filter(text => {
          const age = now - text.timestamp;
          // Remove texts that are:
          // 1. Older than 1 second
          // 2. Have invalid timestamps
          // 3. Have undefined properties
          return age < 1000 && 
                 age >= 0 && 
                 text.id && 
                 text.timestamp && 
                 text.x !== undefined && 
                 text.y !== undefined;
        });
        
        // If we somehow got too many, trim the array
        return filtered.slice(-20);
      });
    }, 33); // Run at ~30fps for smoother cleanup

    return () => clearInterval(cleanup);
  }, []);

  // Add these near the top with other state declarations
  // Initialize dark mode immediately to prevent flash
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    const isDark = savedTheme === 'dark';
    // Apply immediately to prevent flash
    if (isDark) {
      document.body.classList.add('dark-mode');
    }
    return isDark;
  });

  // Add this effect to handle theme changes
  useEffect(() => {
    document.body.classList.toggle('dark-mode', isDarkMode);
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Add this function to handle theme toggle
  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };
  
  // Add keyboard support for canceling placement
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && placingStructure) {
        setPlacingStructure(null);
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [placingStructure]);

  // Add this helper function near your other utility functions
  const getWeatherDescription = (weather: Weather): string => {
    switch (weather.type) {
      case 'sunny':
        return 'Perfect growing conditions. Growth speed is normal.';
      case 'rainy':
        return 'Plants grow 50% faster! Even faster with Advanced Irrigation.';
      case 'stormy':
        return 'Bad weather slows growth by 50%.';
      case 'drought':
        return 'Severe conditions slow growth by 75%.';
      default:
        return '';
    }
  };

  // Add to App component state
  const [mills, setMills] = useState<MillData[]>([]);
  const [selectedMill, setSelectedMill] = useState<SelectedMill | null>(null);

  // Find the existing handleMillToggle function and replace it with this:
  const handleMillToggle = (millId: string) => {
    console.log('Toggling mill:', millId); // Debug log
    setMills(prev => {
      const newMills = prev.map(mill => {
        if (mill.id === millId) {
          const newActive = !mill.active;
          console.log(`Setting mill ${millId} active state to:`, newActive); // Debug log
          return { 
            ...mill, 
            active: newActive,
            progress: newActive ? mill.progress : 0 
          };
        }
        return mill;
      });
      console.log('Updated mills:', newMills); // Debug log
      return newMills;
    });
  };

  // Find the existing mill processing effect and update it:
  useEffect(() => {
    const millTimer = setInterval(() => {
      setMills(prevMills => {
        let updated = false;
        const newMills = prevMills.map(mill => {
          if (!mill.active || inventory.wheat < 5) return mill;
          
          const processTime = Math.max(50 - mill.level * 5, 10);
          const newProgress = mill.progress + 1;
          
          if (newProgress >= processTime) {
            if (!updated) {
              setInventory(prev => ({
                ...prev,
                wheat: prev.wheat - 5,
                flour: prev.flour + Math.floor(1 * (1 + mill.level * 0.2))
              }));
              updated = true;
            }
            return { ...mill, progress: 0 };
          }
          
          return { ...mill, progress: newProgress };
        });
        
        return newMills;
      });
    }, 1000);

    return () => clearInterval(millTimer);
  }, [inventory.wheat, mills]); // Add mills as dependency

  // Add these handler functions inside the App component
  const handleMillUpgrade = (millId: string) => {
    setMills(prev => {
      const millToUpgrade = prev.find(m => m.id === millId);
      if (!millToUpgrade) return prev;

      const upgradeCost = {
        bricks: 3 + millToUpgrade.level * 2,
        tools: 1 + Math.floor(millToUpgrade.level / 2)
      };

      const canAfford = Object.entries(upgradeCost).every(
        ([resource, amount]) => inventory[resource as keyof typeof inventory] >= amount
      );

      if (!canAfford) return prev;

      setInventory(prevInv => {
        const newInv = { ...prevInv };
        Object.entries(upgradeCost).forEach(([resource, amount]) => {
          const key = resource as keyof typeof inventory;
          newInv[key] -= amount;
        });
        return newInv;
      });

      return prev.map(mill => 
        mill.id === millId 
          ? { ...mill, level: mill.level + 1 }
          : mill
      );
    });
  };

  // Add to App component state
  const [ovens, setOvens] = useState<OvenData[]>([]);
  const [selectedOven, setSelectedOven] = useState<SelectedOven | null>(null);

  // Add these handler functions inside App component
  const handleOvenToggle = (ovenId: string) => {
    setOvens(prev => prev.map(oven => 
      oven.id === ovenId 
        ? { ...oven, active: !oven.active, progress: !oven.active ? oven.progress : 0 }
        : oven
    ));
  };

  const handleOvenUpgrade = (ovenId: string) => {
    setOvens(prev => {
      const ovenToUpgrade = prev.find(o => o.id === ovenId);
      if (!ovenToUpgrade) return prev;

      const upgradeCost = {
        bricks: 3 + ovenToUpgrade.level * 2,
        tools: 1 + Math.floor(ovenToUpgrade.level / 2)
      };

      const canAfford = Object.entries(upgradeCost).every(
        ([resource, amount]) => inventory[resource as keyof typeof inventory] >= amount
      );

      if (!canAfford) return prev;

      setInventory(prevInv => {
        const newInv = { ...prevInv };
        Object.entries(upgradeCost).forEach(([resource, amount]) => {
          const key = resource as keyof typeof inventory;
          newInv[key] -= amount;
        });
        return newInv;
      });

      return prev.map(oven => 
        oven.id === ovenId 
          ? { ...oven, level: oven.level + 1 }
          : oven
      );
    });
  };

  // Add oven processing effect
  useEffect(() => {
    const ovenTimer = setInterval(() => {
      setOvens(prevOvens => {
        let updated = false;
        const newOvens = prevOvens.map(oven => {
          if (!oven.active || inventory.flour < 3) return oven;
          
          const processTime = Math.max(40 - oven.level * 4, 8);
          const newProgress = oven.progress + 1;
          
          if (newProgress >= processTime) {
            if (!updated) {
              setInventory(prev => ({
                ...prev,
                flour: prev.flour - 3,
                bread: prev.bread + Math.floor(1 * (1 + oven.level * 0.2))
              }));
              updated = true;
            }
            return { ...oven, progress: 0 };
          }
          
          return { ...oven, progress: newProgress };
        });
        
        return newOvens;
      });
    }, 1000);

    return () => clearInterval(ovenTimer);
  }, [inventory.flour]);

  // Add data-weather to body for global effects
  useEffect(() => {
    document.body.setAttribute('data-weather', weather.type);
    return () => {
      document.body.removeAttribute('data-weather');
    };
  }, [weather.type]);

  return (
    <div className="app-container" data-weather={weather.type}>
      <button 
        className="theme-toggle" 
        onClick={toggleTheme}
        title={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}
      >
        {isDarkMode ? '☀️' : '🌙'}
      </button>
      <div className="header-section">
        <h1 className="title">🌾 Humble Farming 🚜</h1>
        <p className="subtitle">A relaxing idle farming adventure</p>
        <a 
          href="https://pump.fun/coin/DiTENoxr5ukvVHte77LMrThYMP74BbTbmwJVWshUpump" 
          target="_blank" 
          rel="noopener noreferrer"
          className="promo-button"
        >
          Buy <span className="token-name">$humble</span> on pump.fun
        </a>
      </div>
      
      <div className="grid">
        {grid.map((row, rowIndex) => (
          <div key={rowIndex} className="row">
            {row.map((cell, cellIndex) => (
              <span
                key={cellIndex}
                className={`cell ${
                  placingStructure && cell === "🟫" 
                    ? hoveredCell?.row === rowIndex && hoveredCell?.col === cellIndex
                      ? 'placeable-hover'
                      : 'placeable'
                    : cell === "⚙️" || cell === "🏭" || cell === "🏗️" || cell === "🏪"
                    ? 'structure'
                    : cell === "🟫"
                    ? 'empty'
                    : cell === "🌾"
                    ? 'growing'
                    : ''
                }`}
                onClick={(event) => handleTileClick(rowIndex, cellIndex, event)}
                onMouseEnter={() => placingStructure && cell === "🟫" && setHoveredCell({row: rowIndex, col: cellIndex})}
                onMouseLeave={() => placingStructure && setHoveredCell(null)}
              >
                {placingStructure && cell === "🟫" && hoveredCell?.row === rowIndex && hoveredCell?.col === cellIndex ? (
                  <span className="structure-preview">
                    {placingStructure === "millStructure" ? "⚙️" :
                     placingStructure === "ovenStructure" ? "🏭" :
                     placingStructure === "silo" ? "🏗️" :
                     placingStructure === "marketStall" ? "🏪" : ""}
                  </span>
                ) : cell}
                {cell === "🌾" && (
                  <GrowthProgress row={rowIndex} col={cellIndex} />
                )}
              </span>
            ))}
          </div>
        ))}
      </div>
      <div className="buttons">
        <button
          className={`game-button ${selectedAction === "harvest" ? "active" : ""}`}
          onClick={() => {
            setSelectedAction("harvest");
            setPlacingStructure(null);
          }}
        >
          🔪 Harvest
        </button>
        <button
          className={`game-button ${selectedAction === "till" ? "active" : ""}`}
          onClick={() => {
            setSelectedAction("till");
            setPlacingStructure(null);
          }}
          disabled={inventory.fiber < tillingCost}
        >
          ⛏️ Till
        </button>
        <button
          className={`game-button ${selectedAction === "plant" ? "active" : ""}`}
          onClick={() => {
            setSelectedAction("plant");
            setPlacingStructure(null);
          }}
          disabled={inventory.wheatSeeds <= 0}
        >
          🌱 Plant
        </button>
        {placingStructure && (
          <button
            className="game-button active"
            onClick={() => setPlacingStructure(null)}
          >
            ❌ Cancel Placement
          </button>
        )}
      </div>
      <div className="weather-container">
        <div className="weather-emoji">
          {weather.type === 'sunny' && '☀️'}
          {weather.type === 'rainy' && '🌧️'}
          {weather.type === 'stormy' && '⛈️'}
          {weather.type === 'drought' && '🏜️'}
        </div>
        <div className="weather-tooltip">
          <div className="weather-tooltip-header">
            <span className="weather-tooltip-icon">
              {weather.type === 'sunny' && '☀️'}
              {weather.type === 'rainy' && '🌧️'}
              {weather.type === 'stormy' && '⛈️'}
              {weather.type === 'drought' && '🏜️'}
            </span>
            <strong>{weather.type.charAt(0).toUpperCase() + weather.type.slice(1)} Weather</strong>
          </div>
          <div className="weather-tooltip-effect">
            <div className="effect-label">Growth Speed:</div>
            <div className={`effect-value ${weather.modifier > 1 ? 'positive' : weather.modifier < 1 ? 'negative' : ''}`}>
              {weather.modifier > 1 ? '⬆️' : weather.modifier < 1 ? '⬇️' : '➡️'} {(weather.modifier * 100).toFixed(0)}%
            </div>
          </div>
          <div className="weather-description">
            {getWeatherDescription(weather)}
          </div>
          <div className="weather-duration">
            Changes in ~{weather.duration}s
          </div>
        </div>
      </div>
      <div className="resources">
        {/* Basic Resources */}
        <div className="resource-group">
          <h3>Basic Resources</h3>
          <div className="resource">🌿 Fiber: {inventory.fiber}</div>
          <div className="resource">🌱 Wheat Seeds: {inventory.wheatSeeds}</div>
          <div className="resource">🟤 Clay: {inventory.clay}</div>
          <div className="resource">🪨 Rocks: {inventory.rocks}</div>
          <div className="resource">🪵 Wood: {inventory.wood}</div>
          <div className="resource">🌾 Wheat: {inventory.wheat}</div>
        </div>
        
        {/* Crafted Items */}
        <div className="resource-group">
          <h3>Crafted Items</h3>
          <div className="resource">🧱 Bricks: {inventory.bricks}</div>
          <div className="resource">🔨 Tools: {inventory.tools}</div>
          <div className="resource">❄️ Flour: {inventory.flour}</div>
          <div className="resource">🍞 Bread: {inventory.bread}</div>
        </div>
        
        {/* Structures - New Section */}
        <div className="resource-group">
          <h3>Structures</h3>
          <div className="resource">⚙️ Mills: {mills.length}</div>
          <div className="resource">🏭 Ovens: {inventory.ovenStructure}</div>
          <div className="resource">🏗️ Silos: {inventory.silo}</div>
          <div className="resource">🏪 Markets: {inventory.marketStall}</div>
        </div>
        
        {/* Currency */}
        <div className="resource-group">
          <h3>Currency</h3>
          <div className="resource">💰 Coins: {inventory.coins}</div>
          <div className="resource">💎 Gems: {inventory.gems}</div>
        </div>
      </div>
      <div className={`prestige-section ${inventory.wheat >= 1000 ? 'can-prestige' : ''}`}>
        <button 
          onClick={prestige}
          disabled={inventory.wheat < 1000}
          title={inventory.wheat < 1000 ? 
            `Need ${1000 - inventory.wheat} more wheat to prestige` : 
            'Reset progress for permanent bonuses'
          }
        >
          Prestige
        </button>
        <div>Points: {prestigePoints}</div>
        <div>Multiplier: {prestigeMultiplier.toFixed(1)}x</div>
      </div>

      <div className="upgrades-section">
        <h2>Upgrades</h2>
        <div className="upgrades-grid">
          {upgrades
            .filter(upgrade => {
              if (!upgrade.requires) return true;
              return upgrade.requires.every(req => {
                const requiredUpgrade = upgrades.find(u => u.name === req);
                return requiredUpgrade?.purchased;
              });
            })
            .map(upgrade => {
              const canAfford = Object.entries(upgrade.cost).every(
                ([resource, amount]) => inventory[resource as keyof typeof inventory] >= amount
              );
              const isMaxLevel = !!(upgrade.maxLevel && (upgrade.level || 0) >= upgrade.maxLevel);
              
              return (
                <div 
                  key={upgrade.name} 
                  className={`upgrade-card ${upgrade.purchased && !upgrade.maxLevel ? 'purchased' : ''} ${isMaxLevel ? 'max-level' : ''}`}
                >
                  <h3>{upgrade.name}</h3>
                  {upgrade.maxLevel && <div className="upgrade-level">Level: {upgrade.level || 0}/{upgrade.maxLevel}</div>}
                  <p className="upgrade-description">{upgrade.description}</p>
                  <div className="upgrade-cost">
                    Cost: {Object.entries(upgrade.cost).map(([resource, amount]) => (
                      <span 
                        key={resource}
                        className={inventory[resource as keyof typeof inventory] >= amount ? 'has-resources' : 'missing-resources'}
                      >
                        {getEmoji(resource)} {amount} {resource}
                      </span>
                    ))}
                  </div>
                  <button 
                    className="upgrade-button"
                    onClick={() => purchaseUpgrade(upgrade)}
                    disabled={!canAfford || isMaxLevel}
                  >
                    {isMaxLevel ? 'Max Level' : upgrade.purchased && !upgrade.maxLevel ? 'Purchased' : 'Purchase'}
                  </button>
                  <div className="upgrade-tier-badge">Tier {upgrade.tier}</div>
                </div>
              );
            })}
        </div>
      </div>

      <div className="crafting-section">
        <h2>Crafting</h2>
        <div className="recipe-grid">
          {recipes
            .filter(r => r.unlocked && r.type === "craft")
            .map(recipe => (
              <RecipeCard 
                key={recipe.name} 
                recipe={recipe} 
                onCraft={(recipe) => {
                  craftRecipe(recipe);
                  setSelectedAction(null); // Clear other actions when crafting
                }}
                inventory={inventory}
                addFloatingText={addFloatingText}
              />
            ))}
        </div>
      </div>

      {inventory.marketStall > 0 && (
        <div className="market-section">
          <h2>Market</h2>
          <div className="recipe-grid">
            {recipes
              .filter(r => r.unlocked && r.type === "market")
              .map(recipe => (
                <MarketCard 
                  key={recipe.name} 
                  recipe={recipe} 
                  onSell={sellItem}
                  inventory={inventory}
                />
              ))}
          </div>
        </div>
      )}

      <div className="quest-section">
        <h2>Quests</h2>
        <div className="quest-grid">
          {quests
            .filter(q => !q.completed && !q.hidden)
            .map(quest => {
              const isReadyToClaim = completedUnclaimedQuests.includes(quest.id);
              const isClaiming = claimingQuest === quest.id;
              
              return (
                <div 
                  key={quest.id} 
                  className={`quest-card ${isReadyToClaim ? 'quest-ready' : ''} ${isClaiming ? 'quest-claiming' : ''}`}
                >
                  <h3>{quest.name}</h3>
                  <p>{quest.description}</p>
                  <div className="quest-rewards">
                    {Object.entries(quest.reward).map(([item, amount]) => (
                      <span key={item}>{getEmoji(item)} {amount} {item}</span>
                    ))}
                  </div>
                  {isReadyToClaim && (
                    <button 
                      className="claim-button"
                      onClick={() => claimQuest(quest.id)}
                      disabled={isClaiming}
                    >
                      {isClaiming ? '✨ Claiming...' : '🎉 Claim Reward!'}
                    </button>
                  )}
                  <div className="quest-tier">Tier {quest.tier}</div>
                  {isReadyToClaim && <div className="quest-complete-badge">✓ Complete!</div>}
                </div>
              );
            })}
        </div>
      </div>
      
      {/* Quest completion notifications */}
      {completedUnclaimedQuests.length > 0 && (
        <div className="quest-notifications">
          {completedUnclaimedQuests.slice(0, 3).map(questId => {
            const quest = quests.find(q => q.id === questId);
            if (!quest) return null;
            
            return (
              <div key={questId} className="quest-notification" onClick={() => claimQuest(questId)}>
                <div className="notif-icon">🎉</div>
                <div className="notif-content">
                  <div className="notif-title">Quest Complete!</div>
                  <div className="notif-quest">{quest.name}</div>
                  <div className="notif-rewards">
                    {Object.entries(quest.reward).map(([item, amount]) => (
                      <span key={item}>{getEmoji(item)} +{amount}</span>
                    ))}
                  </div>
                </div>
                <div className="notif-claim">Click to Claim</div>
              </div>
            );
          })}
        </div>
      )}
      {placingStructure && (
        <div className="placing-structure-overlay">
          <div className="placing-structure-indicator">
            <div className="indicator-icon">
              {placingStructure === "millStructure" ? "⚙️" :
               placingStructure === "ovenStructure" ? "🏭" :
               placingStructure === "silo" ? "🏗️" :
               placingStructure === "marketStall" ? "🏪" : ""}
            </div>
            <div className="indicator-text">
              <strong>Placing: {
                placingStructure === "millStructure" ? "Wheat Mill" :
                placingStructure === "ovenStructure" ? "Baker's Oven" :
                placingStructure === "silo" ? "Storage Silo" :
                placingStructure === "marketStall" ? "Market Stall" : ""
              }</strong>
              <p>Click an empty tile (🟫) to place</p>
              <p className="hint">Press ESC to cancel</p>
            </div>
          </div>
        </div>
      )}
      {floatingTexts.map(notification => (
        <FloatingNotification key={notification.id} notification={notification} />
      ))}
      {selectedMill && (
        <MillMenu
          mill={mills.find(mill => mill.id === selectedMill.millId)!}
          screenPosition={selectedMill.screenPosition}
          onClose={() => setSelectedMill(null)}
          onToggle={handleMillToggle}
          onUpgrade={handleMillUpgrade}
          inventory={inventory}
        />
      )}
      {selectedOven && (
        <OvenMenu
          oven={ovens.find(oven => oven.id === selectedOven.ovenId)!}
          screenPosition={selectedOven.screenPosition}
          onClose={() => setSelectedOven(null)}
          onToggle={handleOvenToggle}
          onUpgrade={handleOvenUpgrade}
          inventory={inventory}
        />
      )}
    </div>
  );
};

export default App;
